import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { QuestionnaireFormData, CalculatedResults } from "@/constants/questionnaire";

export interface OrganizationQuestionnaire {
  id: string;
  organization_id: string;
  created_by: string | null;
  form_data: QuestionnaireFormData;
  calculated_results: CalculatedResults;
  created_at: string;
  updated_at: string;
}

export const useOrganizationQuestionnaires = (organizationId: string) => {
  return useQuery({
    queryKey: ["organization-questionnaires", organizationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organization_questionnaires" as any)
        .select("*")
        .eq("organization_id", organizationId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return (data || []) as unknown as OrganizationQuestionnaire[];
    },
    enabled: !!organizationId,
  });
};

export const useCreateQuestionnaire = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      organizationId,
      formData,
      calculatedResults,
    }: {
      organizationId: string;
      formData: QuestionnaireFormData;
      calculatedResults: CalculatedResults;
    }) => {
      const { data: userData } = await supabase.auth.getUser();

      const { data, error } = await supabase
        .from("organization_questionnaires" as any)
        .insert({
          organization_id: organizationId,
          created_by: userData.user?.id || null,
          form_data: formData as any,
          calculated_results: calculatedResults as any,
        })
        .select()
        .single();

      if (error) throw error;
      return data as unknown as OrganizationQuestionnaire;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["organization-questionnaires", variables.organizationId],
      });
      toast.success("Questionnaire saved successfully");
    },
    onError: (error) => {
      toast.error("Failed to save questionnaire: " + error.message);
    },
  });
};

export const useUpdateQuestionnaire = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      organizationId,
      formData,
      calculatedResults,
    }: {
      id: string;
      organizationId: string;
      formData: QuestionnaireFormData;
      calculatedResults: CalculatedResults;
    }) => {
      const { data, error } = await supabase
        .from("organization_questionnaires" as any)
        .update({
          form_data: formData as any,
          calculated_results: calculatedResults as any,
        })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data as unknown as OrganizationQuestionnaire;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["organization-questionnaires", variables.organizationId],
      });
      toast.success("Questionnaire updated successfully");
    },
    onError: (error) => {
      toast.error("Failed to update questionnaire: " + error.message);
    },
  });
};

export const useDeleteQuestionnaire = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      organizationId,
    }: {
      id: string;
      organizationId: string;
    }) => {
      const { error } = await supabase
        .from("organization_questionnaires" as any)
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["organization-questionnaires", variables.organizationId],
      });
      toast.success("Questionnaire deleted");
    },
    onError: (error) => {
      toast.error("Failed to delete questionnaire: " + error.message);
    },
  });
};
